import React, { useState, useEffect } from 'react';
import { X, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';

interface ErrorDisplayProps {
  error: string | null;
  onDismiss?: () => void;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ error, onDismiss }) => {
  const [displayError, setDisplayError] = useState<string | null>(null);

  useEffect(() => {
    if (error) {
      setDisplayError(error);

      const timeoutId = setTimeout(() => {
        setDisplayError(null);
      }, 100000);

      return () => clearTimeout(timeoutId);
    }
  }, [error]);

  const handleDismiss = () => {
    setDisplayError(null);
    if (onDismiss) {
      onDismiss();
    }
  };

  if (!displayError) {
    return null;
  }

  return (
    <div className="relative rounded-lg border border-destructive/50 bg-destructive/10 p-4 mb-4 text-destructive">
      <div className="flex gap-3 pr-8">
        <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
        <div className="text-sm">
          <span className="font-semibold">Error:</span>{' '}
          {displayError.split('\n').map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </div>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2 h-6 w-6 text-destructive hover:bg-destructive/20"
        onClick={handleDismiss}
        aria-label="Dismiss error"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};

export default ErrorDisplay;
